<template>
    <pendo-multiselect
        ref="multiselect"
        :placeholder="$t('eventPropertyFilter.Select or type in values')"
        taggable
        searchable
        :tag-placeholder="$t('eventPropertyFilter.new')"
        full-width
        min-menu-width="400"
        :multiple="multiple"
        :hide-selected-options="true"
        :value="value"
        :options="allOptions"
        :close-on-select="!multiple"
        :invalid="invalid"
        @tag="onCustomInput"
        @input="onInput"
        @input-value-change="$emit('input-value-change', $event)"
        @open="$emit('open')"
        @closed="$emit('closed')">
        <template #trigger="{ comboboxAttrs, handleKeydown, setTriggerRef }">
            <event-property-filter-tag
                :ref="setTriggerRef"
                v-bind="comboboxAttrs"
                :aria-label="$attrs['aria-label']"
                truncate-tag-label
                :value="value"
                :is-open="isOpen"
                :disabled="disabled"
                :applicable="applicable"
                tabindex="0"
                @click="toggleMenu"
                @keydown="handleKeydown" />
        </template>
        <template #header="{ comboboxAttrs, handleKeydown }">
            <div class="event-property-value-search">
                <pendo-multiselect-value v-bind="multiselectCore">
                    <template #control>
                        <pendo-multiselect-input
                            v-bind="{ ...multiselectCore, ...comboboxAttrs }"
                            :aria-label="$attrs['aria-label']"
                            @keydown="handleKeydown" />
                    </template>
                </pendo-multiselect-value>
            </div>
        </template>
        <!-- eslint-disable vue/no-v-html -->
        <template #noData>
            <pendo-alert
                v-if="!loading && isMetadata"
                :show-icon="false"
                center
                type="warning">
                <span v-html="metadataWarningText" />
            </pendo-alert>
            <div
                v-if="!loading"
                class="no-data-message">
                {{ $t('eventPropertyFilter.No options available') }}
            </div>
            <div
                v-if="loading"
                class="no-data-message">
                <pendo-loading-indicator
                    loading
                    size="small" />
            </div>
        </template>
        <template #noResult>
            <pendo-alert
                v-if="isMetadata"
                :show-icon="false"
                center
                type="warning">
                <span v-html="metadataWarningText" />
            </pendo-alert>
            <div class="no-data-message">
                {{ $t('eventPropertyFilter.No options available') }}
            </div>
        </template>
        <template #allSelected>
            <pendo-alert
                v-if="isMetadata"
                :show-icon="false"
                center
                type="warning">
                <span v-html="metadataWarningText" />
            </pendo-alert>
            <div
                v-if="!isMetadata"
                class="no-data-message">
                {{ $t('eventPropertyFilter.All available options selected') }}
            </div>
        </template>
        <!-- eslint-enable vue/no-v-html -->
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoMultiselectInput, PendoMultiselectValue, PendoLoadingIndicator, PendoAlert } from '@pendo/components';
import EventPropertyFilterTag from './event-property-filter-tag.vue';
import { coerceEventPropertyType } from './utils';

export default {
    name: 'EventPropertyValueMultiselect',
    components: {
        EventPropertyFilterTag,
        PendoMultiselect,
        PendoMultiselectInput,
        PendoMultiselectValue,
        PendoLoadingIndicator,
        PendoAlert,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        applicable: {
            type: Boolean,
            default: false,
        },
        isMetadata: {
            type: Boolean,
            default: false,
        },
        schema: {
            type: String,
            default: 'string',
        },
        elementType: {
            type: String,
            default: 'string',
        },
    },
    emits: ['input', 'open', 'closed', 'input-value-change'],
    data () {
        return {
            isOpen: false,
            invalid: false,
        };
    },
    computed: {
        metadataWarningText () {
            return this.$t('eventPropertyFilter.Enter any value, but please note that you can only search for existing values that have the metadata type set to Text in Metadata (under Settings).');
        },
        customOptions () {
            return this.value.filter((val) => !this.options.includes(val));
        },
        allOptions () {
            const optionsToString = this.options.map((option) => {
                const { coercedValue, isValid } = coerceEventPropertyType(option, this.coerceSchema);
                if (!isValid) {
                    return {
                        label: coercedValue,
                        disabled: true,
                    };
                }

                return coercedValue;
            });

            return this.customOptions.concat(optionsToString);
        },
        coerceSchema () {
            return this.schema === 'list' ? this.elementType : this.schema;
        },
        multiselectCore () {
            if (this.$refs.multiselect?.$refs.core) {
                const {
                    computedPlaceholder,
                    getOptionLabel,
                    inputValue,
                    isFullWidth,
                    isOpen,
                    isPlaceholderVisible,
                    model,
                    placeholder,
                    removeSelection,
                    selectedLabel,
                    selectedValue,
                    selectedValuePointer,
                    showSelectedValueTags,
                    toggleMenu,
                    updateInputValue,
                    visibleSelectedValues,
                } = this.$refs.multiselect.$refs.core;

                return {
                    computedPlaceholder,
                    getOptionLabel,
                    inputValue,
                    isFullWidth,
                    isOpen,
                    isPlaceholderVisible,
                    model,
                    placeholder,
                    removeSelection,
                    selectedLabel,
                    selectedValue,
                    selectedValuePointer,
                    showSelectedValueTags,
                    toggleMenu,
                    updateInputValue,
                    visibleSelectedValues,
                };
            }

            return {};
        },
    },
    methods: {
        // used by the parent component to focus the trigger
        // eslint-disable-next-line internal-rules/no-unused-properties
        getTriggerRef () {
            return this.$refs.multiselect.$refs.core.triggerRef;
        },
        onInput (value) {
            this.invalid = false;
            if (!Array.isArray(value)) {
                value = [value];
            }
            this.$emit('input', value);
        },
        onCustomInput (id) {
            const { coercedValue, isValid } = coerceEventPropertyType(id, this.coerceSchema);
            if (!isValid) {
                this.invalid = true;

                return;
            }
            if (this.multiple) {
                this.$emit('input', [...this.value, coercedValue]);
            } else {
                this.$emit('input', [coercedValue]);
            }
            this.invalid = false;
        },
        toggleMenu () {
            this.$refs.multiselect.toggleMenu();
        },
    },
};
</script>

<style lang="less" scoped>
.event-property-value-search {
    outline: 2px solid transparent;
    outline-offset: 2px;
    margin: 4px;
    border: 1px solid var(--palette-gray-100);
    border-radius: 3px;
    padding: 0 4px;

    &:focus-within {
        outline-color: var(--palette-gray-100);
    }
}

.no-data-message {
    text-align: center;
    color: var(--palette-gray-70);
    padding-top: 16px;
    padding-bottom: 16px;
}
</style>
